import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FolderCheck, ScanLine, RefreshCw, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { IUBENDA_COOKIE_URL, IUBENDA_PRIVACY_URL, openIubendaPreferences } from "@/lib/iubenda";
import "./landing-contrast.css";

/* ==========================================================================
   Cosmely — Home page (brief 10 aree)
   Palette UI: contrasti monocromatici locali; fotografie mantenute originali.
   Font:    Cormorant Garamond (serif) · Outfit (sans)
   Pulsante pieno: .btn-buy-now
   ========================================================================== */

const storedAsset = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`;
const IMG = {
  packaging: storedAsset("14fcba0f-079b-4bae-ac2d-ad215194d760"),
  productSheet: storedAsset("ac5524e1-bb86-42e2-b9cc-76962030c8d8"),
  document: storedAsset("965a1932-f42f-46ff-9108-da96b63a812e"),
  privateLabel: storedAsset("5f57c710-96c5-49e7-9fb3-7d6b592e508c"),
  supplier: storedAsset("34a08cb9-53a5-4b47-a12e-71f781931cf1"),
  qrDark: storedAsset("4c9cd26f-0341-4d3f-a3de-e6ce0cff29c1"),
  catalog: storedAsset("7e4d60ce-559a-4f57-b0fb-cd627a2aba38"),
  producer: storedAsset("a9fa4c74-053f-41a8-b7e4-ebbbef92a835"),
  indieBrand: storedAsset("07f0cfb5-55f6-4d50-8a19-481e20cfafde"),
  contractMfg: storedAsset("d197a73e-a6cb-4c4d-ac69-dc9e96dd7011"),
};

/* ------------------------------ Utility ---------------------------------- */
function Reveal({ children, className = "", delay = 0, y = 24 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
function Eyebrow({ children, className = "" }) {
  return <div className={`text-[10px] tracking-[0.32em] uppercase text-[#8A8579] ${className}`}>{children}</div>;
}

/* ==========================================================================
   HEADER (sticky)
   ========================================================================== */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const items = [
    { href: "#cosa-fa", label: "Cosa fa" },
    { href: "#conto-terzi", label: "Conto terzi" },
    { href: "#per-chi", label: "Per chi è" },
  ];
  return (
    <header
      className={`landing-nav fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "landing-nav--scrolled" : "border-b border-transparent"}`}
      data-testid="landing-nav"
    >
      <nav className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="font-serif text-xl md:text-2xl tracking-tight text-[#1A1A1A]" data-testid="landing-brand">Cosmely</a>

        <div className="hidden md:flex items-center gap-10">
          {items.map((it) => (
            <a key={it.href} href={it.href} className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A] hover:text-[#8A8579] transition-colors" data-testid={`nav-${it.href.slice(1)}`}>{it.label}</a>
          ))}
        </div>

        <div className="landing-nav-actions flex items-center gap-4 md:gap-6">
          <span className="hidden md:block w-[1px] h-5 bg-[#E5E5E0]"></span>
          <Link to="/login" className="hidden md:inline-block text-[10px] tracking-[0.28em] uppercase text-[#8A8579] hover:text-[#1A1A1A] transition-colors" data-testid="nav-login">Accedi</Link>
          <a href="#demo" className="landing-nav-cta btn-buy-now text-[10px] !py-3 !px-6 md:!px-8" data-testid="nav-demo">Richiedi una demo</a>
          <button className="md:hidden text-[#1A1A1A]" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
            <div className="w-6 h-[1.5px] bg-[#1A1A1A] mb-1.5"></div>
            <div className="w-6 h-[1.5px] bg-[#1A1A1A] mb-1.5"></div>
            <div className="w-4 h-[1.5px] bg-[#1A1A1A]"></div>
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-[#FAF9F6] border-t border-[#E5E5E0]" data-testid="nav-mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-6">
            {items.map((it) => (
              <a key={it.href} href={it.href} onClick={() => setOpen(false)} className="text-xs tracking-[0.28em] uppercase text-[#1A1A1A]" data-testid={`nav-mobile-${it.href.slice(1)}`}>{it.label}</a>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="text-xs tracking-[0.28em] uppercase text-[#8A8579]" data-testid="nav-mobile-login">Accedi</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ==========================================================================
   01 — APERTURA
   ========================================================================== */
function Hero() {
  return (
    <section id="top" className="landing-section landing-section--hero relative overflow-hidden">
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center">
          <Reveal delay={0.1}>
            <div>
              <h1 className="font-serif text-[38px] sm:text-[52px] md:text-[60px] lg:text-[68px] leading-[1.03] tracking-[-0.01em] text-[#1A1A1A]"
                  data-testid="hero-heading">
                Tutto sotto controllo.<br data-testid="hero-title-line-break" />
                <em className="text-[#8A8579]">Sempre un passo avanti.</em>
              </h1>
              <p className="mt-8 text-[16px] md:text-[18px] text-[#4A4A4A] leading-relaxed max-w-xl"
                 data-testid="hero-description">
                Cosmely tiene insieme dati, ingredienti, documenti, scadenze e aggiornamenti, così sai sempre cosa hai, cosa manca e qual è la versione giusta. E con QR dinamici, cataloghi digitali e strumenti di brandizzazione, quello che comunichi resta sempre aggiornato — anche dopo che il prodotto è già sul mercato.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a href="#demo" className="btn-buy-now text-[10px] text-center" data-testid="hero-cta-demo">Richiedi una demo</a>
                <a href="#cosa-fa" className="inline-flex items-center justify-center px-11 py-4 border border-[#1A1A1A] text-[#1A1A1A] text-[10px] tracking-[0.28em] uppercase hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors" data-testid="hero-cta-see">Guarda come funziona</a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="relative aspect-[4/3] rounded-[8px] overflow-hidden border border-[#E5E5E0] shadow-[0_40px_120px_-40px_rgba(26,26,26,0.35)]">
              <img src={IMG.packaging} alt="Prodotto cosmetico con QR, catalogo digitale, ingredienti e documenti collegati" className="w-full h-full object-cover" data-testid="hero-image" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   07 — COSA CAMBIA (3 colonne con icona)
   ========================================================================== */
function WhatChanges() {
  const items = [
    { Icon: FolderCheck, t: "Tutti i documenti in un posto solo", d: "CPNP, schede di sicurezza, notifiche ai portali europei, PIF. Ogni prodotto ha i suoi, e li ritrovi in dieci secondi." },
    { Icon: ScanLine, t: "Niente da ricopiare", d: "L'AI legge gli ingredienti dal documento che ti manda il laboratorio e compila la scheda del prodotto. Tu controlli e approvi prima che diventi definitiva." },
    { Icon: RefreshCw, t: "Una sola versione, quella giusta", d: "Cambi un dato e cambia ovunque: schede, pagina pubblica, documenti dei clienti conto terzi." },
  ];
  return (
    <section className="landing-section landing-section--dark py-24 md:py-36" data-testid="landing-changes-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <h2 className="max-w-3xl font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A]">
            Cosa cambia, dal primo prodotto che <em className="text-[#8A8579]">carichi</em>
          </h2>
        </Reveal>
        <div className="mt-16 md:mt-20 grid md:grid-cols-3 gap-12 md:gap-14">
          {items.map(({ Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div data-testid={`whatchanges-card-${i}`}>
                <div className="landing-icon-frame w-12 h-12 rounded-full border border-[#1A1A1A] flex items-center justify-center">
                  <Icon size={20} strokeWidth={1.4} className="text-[#1A1A1A]" />
                </div>
                <h3 className="mt-6 font-serif text-2xl md:text-[26px] text-[#1A1A1A] leading-tight">{t}</h3>
                <p className="mt-4 text-[15px] text-[#4A4A4A] leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   FEATURE ROW — testo/immagine alternati (aree 03-06)
   ========================================================================== */
function FeatureRow({ id, title, paragraphs, image, alt, reverse = false, tone = "white",
                      imageFit = "cover", imageTestId, sectionTestId, headingTestId, action }) {
  return (
    <section id={id} className={`landing-section landing-section--${tone} py-24 md:py-36`} data-testid={sectionTestId}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Testo */}
          <Reveal className={reverse ? "lg:order-2" : "lg:order-1"}>
            <div className="max-w-xl">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[#1A1A1A]"
                  data-testid={headingTestId}>{title}</h2>
              <div className="mt-8 space-y-6">
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-[16px] text-[#4A4A4A] leading-relaxed"
                     data-testid={sectionTestId ? `${sectionTestId}-paragraph-${i}` : undefined}>{p}</p>
                ))}
              </div>
              {action && (
                <a href={action.href} className="btn-buy-now mt-10 inline-block" data-testid={action.testId}>
                  {action.label}
                </a>
              )}
            </div>
          </Reveal>
          {/* Immagine — su mobile sempre sotto il testo */}
          <Reveal delay={0.15} className={reverse ? "lg:order-1" : "lg:order-2"}>
            <div className="landing-image-frame relative aspect-[4/3] rounded-[8px] overflow-hidden border border-[#E5E5E0] shadow-[0_40px_100px_-45px_rgba(26,26,26,0.35)]">
              <img src={image} alt={alt} className={`w-full h-full ${imageFit === "contain" ? "object-contain" : "object-cover"}`}
                   data-testid={imageTestId} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   02 — IL QR (fondo grafite)
   ========================================================================== */
function QRSection() {
  return (
    <section className="landing-section landing-section--dark py-24 md:py-36" data-testid="landing-qr-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="max-w-xl">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                La confezione la stampi una volta. Quello che racconta lo cambi <em className="text-[#525252]">quando vuoi.</em>
              </h2>
              <div className="landing-qr-copy mt-8 space-y-6 text-[16px] leading-relaxed">
                <p>Il QR porta a una pagina che non cambia mai indirizzo: ingredienti, avvertenze, modo d'uso, foto, video, documenti — tutto in un posto solo.</p>
                <p>E quello che c'è dentro lo aggiorni quando ti serve, anche a prodotto già distribuito: aggiungi un tutorial, cambi le immagini, lanci una promozione. Chi inquadra vede subito la versione nuova.</p>
                <p>E chi lo inquadra lo legge nella sua lingua, qualunque sia.</p>
              </div>
              <div className="mt-10">
                <a href="#demo" className="landing-qr-cta inline-block px-12 py-4 text-[10px] tracking-[0.25em] uppercase font-medium rounded-[2px]" data-testid="qr-cta-demo">Richiedi una demo</a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="landing-image-frame relative aspect-[4/3] rounded-[8px] overflow-hidden border border-[#D4D4D4] shadow-[0_40px_100px_-40px_rgba(0,0,0,0.25)]">
              <img src={IMG.qrDark} alt="Un QR stampato sulla confezione resta valido mentre il prodotto cambia" className="w-full h-full object-cover" data-testid="landing-qr-image" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   08 — PER CHI È (3 colonne)
   ========================================================================== */
function ForWho() {
  const items = [
    { img: IMG.producer, t: "Produttori di cosmetici", d: "Decine o centinaia di referenze, ognuna con la sua documentazione. Qui stanno tutte insieme e restano allineate." },
    { img: IMG.indieBrand, t: "Brand indipendenti", d: "Il catalogo, le pagine pubbliche dei prodotti e i documenti da mandare ai rivenditori, in un posto solo." },
    { img: IMG.contractMfg, t: "Contoterzisti", d: "Lo stesso prodotto per dieci clienti diversi, con codici e documentazione intestati ai loro marchi. In automatico." },
  ];
  return (
    <section id="per-chi" className="landing-section landing-section--dark py-24 md:py-36" data-testid="landing-for-who-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A]">
            Fatto per come <em className="text-[#8A8579]">lavori</em>
          </h2>
        </Reveal>
        <div className="mt-16 md:mt-20 grid md:grid-cols-3 gap-10 md:gap-12">
          {items.map(({ img, t, d }, i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div data-testid={`forwho-card-${i}`}>
                <div className="landing-image-frame aspect-[4/3] rounded-[6px] overflow-hidden border border-[#E5E5E0]">
                  <img src={img} alt={t} className="w-full h-full object-cover" data-testid={`forwho-image-${i}`} />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-[#1A1A1A] leading-tight">{t}</h3>
                <p className="mt-4 text-[15px] text-[#4A4A4A] leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   09 — RICHIESTA DEMO (form aperto)
   ========================================================================== */
function DemoForm() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", company_type: "", message: "", consent: false, website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company.trim() || !form.email.trim() || !form.company_type) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    if (!form.consent) {
      toast.error("È necessario accettare l'informativa sulla privacy");
      return;
    }
    setSubmitting(true);
    try {
      const { consent, ...payload } = form;
      await api.post(`/public/demo-request`, payload);
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontattiamo entro 24 ore.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invio fallito. Riprova.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-[#1A1A1A]/25 focus:border-[#1A1A1A] outline-none py-3 text-[15px] text-[#1A1A1A] font-serif placeholder:text-[#8A8579]/50 transition-colors";
  const labelCls = "text-[10px] tracking-[0.28em] uppercase text-[#8A8579]";

  const points = [
    { t: "Una demo sui tuoi prodotti", d: "Non una demo preconfezionata: carichiamo una tua referenza e vedi cosa esce." },
    { t: "Ti rispondiamo entro 24 ore", d: null },
    { t: "Parli con chi Cosmely l'ha costruito", d: "Nessun commerciale a copione, nessun passaggio di mano." },
  ];

  return (
    <section id="demo" className="landing-section landing-section--soft relative py-24 md:py-36" data-testid="demo-form-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-24 items-start">
          <Reveal>
            <div>
              <Eyebrow>Richiedi una demo</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] text-[#1A1A1A]">
                Vediamo Cosmely su un tuo <em className="text-[#8A8579]">prodotto</em>
              </h2>
              <p className="mt-8 text-[16px] text-[#4A4A4A] leading-relaxed max-w-lg">
                Compila il form e ti ricontattiamo per fissare una demo. Niente presentazioni: apriamo la piattaforma e carichiamo un prodotto tuo.
              </p>
              <ul className="mt-10 space-y-6">
                {points.map((p) => (
                  <li key={p.t} className="flex items-start gap-4">
                    <span className="mt-0.5 w-6 h-6 rounded-full border border-[#1A1A1A] flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={1.8} className="text-[#1A1A1A]" />
                    </span>
                    <div>
                      <div className="text-[15px] text-[#1A1A1A] font-medium">{p.t}</div>
                      {p.d && <div className="mt-1 text-[14px] text-[#4A4A4A] leading-relaxed">{p.d}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="landing-demo-panel bg-white border border-[#E5E5E0] rounded-[6px] p-8 md:p-10 shadow-[0_30px_80px_-40px_rgba(26,26,26,0.25)]">
              {done ? (
                <div className="py-16 text-center" data-testid="demo-form-success">
                  <div className="w-14 h-14 mx-auto rounded-full border border-[#1A1A1A] flex items-center justify-center">
                    <Check size={22} strokeWidth={1.6} className="text-[#1A1A1A]" />
                  </div>
                  <div className="mt-8 font-serif text-3xl text-[#1A1A1A] leading-tight">Grazie.</div>
                  <p className="mt-4 text-[15px] text-[#4A4A4A] leading-relaxed max-w-sm mx-auto">
                    Abbiamo ricevuto la tua richiesta. Ti ricontattiamo entro 24 ore all'indirizzo indicato.
                  </p>
                  <button
                    onClick={() => { setDone(false); setForm({ name: "", company: "", email: "", phone: "", company_type: "", message: "", consent: false, website: "" }); }}
                    className="mt-8 text-[10px] tracking-[0.28em] uppercase text-[#8A8579] hover:text-[#1A1A1A] border-b border-[#8A8579] hover:border-[#1A1A1A] pb-1 transition-colors"
                    data-testid="demo-form-reset"
                  >
                    Invia un'altra richiesta
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7" data-testid="demo-form">
                  {/* Honeypot */}
                  <input
                    type="text" name="website" value={form.website} onChange={update("website")}
                    autoComplete="off" tabIndex="-1"
                    style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                    aria-hidden="true"
                  />

                  <div>
                    <label className={labelCls} htmlFor="demo-name">Nome e cognome *</label>
                    <input id="demo-name" type="text" value={form.name} onChange={update("name")}
                      className={inputCls} placeholder="Giulia Bianchi" required data-testid="demo-input-name" />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="demo-company">Azienda *</label>
                    <input id="demo-company" type="text" value={form.company} onChange={update("company")}
                      className={inputCls} placeholder="Nome azienda o brand" required data-testid="demo-input-company" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls} htmlFor="demo-email">Email *</label>
                      <input id="demo-email" type="email" value={form.email} onChange={update("email")}
                        className={inputCls} placeholder="nome@azienda.it" required data-testid="demo-input-email" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="demo-phone">Telefono</label>
                      <input id="demo-phone" type="tel" value={form.phone} onChange={update("phone")}
                        className={inputCls} placeholder="+39 000 000 0000" data-testid="demo-input-phone" />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="demo-type">Che azienda sei *</label>
                    <select id="demo-type" value={form.company_type} onChange={update("company_type")}
                      className={`${inputCls} cursor-pointer`} required data-testid="demo-input-type">
                      <option value="" disabled>Seleziona…</option>
                      <option value="Produttore">Produttore</option>
                      <option value="Brand indipendente">Brand indipendente</option>
                      <option value="Contoterzista">Contoterzista</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="demo-message">Messaggio</label>
                    <textarea id="demo-message" rows={3} value={form.message} onChange={update("message")}
                      className={`${inputCls} resize-none leading-relaxed`}
                      placeholder="Raccontaci brevemente il tuo caso: numero di prodotti, brand, esigenze principali."
                      data-testid="demo-input-message" />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-2">
                    <input type="checkbox" checked={form.consent}
                      onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 accent-[#1A1A1A] shrink-0" required data-testid="demo-input-consent" />
                    <span className="text-[13px] text-[#4A4A4A] leading-relaxed">
                      Ho letto e accetto l'{" "}
                      <a href={IUBENDA_PRIVACY_URL} target="_blank" rel="noreferrer"
                         className="text-[#1A1A1A] underline hover:text-[#8A8579]" data-testid="demo-privacy-link">informativa sulla privacy</a>. *
                    </span>
                  </label>

                  <div className="pt-2">
                    <button
                      type="submit" disabled={submitting}
                      className="btn-buy-now text-[10px] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="demo-submit"
                    >
                      {submitting ? "Invio in corso..." : "Invia richiesta"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
   10 — FOOTER
   ========================================================================== */
function Footer() {
  return (
    <footer className="landing-footer" data-testid="landing-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <div className="font-serif text-3xl" data-testid="footer-brand">Cosmely</div>
            <address className="mt-6 text-sm text-[#525252] leading-relaxed not-italic" data-testid="footer-company-details">
              <span data-testid="footer-company-name">A&amp;J Biocosmetic S.a.s.</span><br />
              <span data-testid="footer-company-street">Via del Mare 38/A,</span><br />
              <span data-testid="footer-company-city">00071 Pomezia (RM)</span><br />
              <span data-testid="footer-company-vat">P.IVA 02887560593</span>
            </address>
          </div>

          <div>
            <div className="landing-footer-label text-[10px] tracking-[0.28em] uppercase" data-testid="footer-sections-heading">Sezioni</div>
            <ul className="mt-5 space-y-3 text-sm">
              {[["Cosa fa", "#cosa-fa"], ["Conto terzi", "#conto-terzi"], ["Per chi è", "#per-chi"], ["Richiedi una demo", "#demo"]].map(([l, h]) => (
                <li key={l}><a href={h} className="text-[#525252] hover:text-[#000000] transition-colors"
                  data-testid={h === "#demo" ? "footer-demo-link" : `footer-section-${h.slice(1)}`}>{l}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="landing-footer-label text-[10px] tracking-[0.28em] uppercase" data-testid="footer-legal-heading">Legale</div>
            <ul className="mt-5 space-y-3 text-sm">
              <li><a href={IUBENDA_PRIVACY_URL} target="_blank" rel="noreferrer" className="text-[#525252] hover:text-[#000000] transition-colors" data-testid="landing-privacy-link">Privacy policy</a></li>
              <li><a href={IUBENDA_COOKIE_URL} target="_blank" rel="noreferrer" className="text-[#525252] hover:text-[#000000] transition-colors" data-testid="landing-cookie-link">Cookie policy</a></li>
              <li><a href="#" onClick={openIubendaPreferences} className="iubenda-cs-preferences-link text-[#525252] hover:text-[#000000] transition-colors" data-testid="landing-cookie-preferences-link">Preferenze cookie</a></li>
              <li><Link to="/termini-di-servizio" className="text-[#525252] hover:text-[#000000] transition-colors" data-testid="landing-terms-link">Termini di servizio</Link></li>
            </ul>
          </div>
        </div>

        <div className="landing-footer-rule mt-16 pt-8 border-t text-[10px] tracking-[0.2em] uppercase">
          © {new Date().getFullYear()} Cosmely
        </div>
      </div>
    </footer>
  );
}

/* ==========================================================================
   PAGE
   ========================================================================== */
export default function Landing() {
  useEffect(() => {
    document.title = "Cosmely — Il gestionale dei prodotti cosmetici";
  }, []);

  return (
    <div className="landing-contrast text-[#1A1A1A]" data-testid="landing-page">
      <Nav />
      <main>
        <Hero />
        <QRSection />
        <FeatureRow
          id="cosa-fa"
          sectionTestId="landing-products-section"
          title={<>Ogni prodotto, con tutto quello che lo <em className="text-[#8A8579]">riguarda</em></>}
          paragraphs={[
            "Anagrafica, ingredienti, foto, avvertenze, modo d'uso, codici a barre, QR. Apri il prodotto e c'è tutto: quello che serve a te e quello che vede chi compra.",
            "E l'AI analizza gli ingredienti: allergeni da segnalare, comedogenicità, per quali tipi di pelle è indicato. Sulla banca dati europea degli ingredienti cosmetici, non a occhio.",
          ]}
          image={IMG.productSheet}
          alt="Analisi ingredienti skincare Lumière e scheda prodotto visibili su Cosmely"
          imageTestId="landing-ingredients-image"
          reverse={false}
        />
        <FeatureRow
          sectionTestId="landing-documents-section"
          title={<>Le schede CPNP le compila Cosmely. Il resto lo tiene in <em className="text-[#8A8579]">ordine.</em></>}
          paragraphs={[
            "Le compila dai dati che hai già inserito e le traduce al volo in sei lingue: non devi mantenere nessuna traduzione. La scheda di sicurezza resta quella del tuo laboratorio: la carichi una volta e Cosmely la tiene versionata accanto al prodotto, sempre l'ultima.",
          ]}
          image={IMG.document}
          alt="Documenti cosmetici multilingue collegati al prodotto e alla scheda di sicurezza"
          imageFit="contain"
          imageTestId="landing-documents-image"
          reverse={true}
          tone="soft"
        />
        <FeatureRow
          id="conto-terzi"
          sectionTestId="landing-private-label-section"
          title={<>Lo stesso prodotto, con il marchio del tuo <em className="text-[#8A8579]">cliente</em></>}
          paragraphs={[
            "Ribrandizzi un tuo prodotto per un cliente? Scegli codice e nome, e tutta la documentazione tecnica si rigenera con i dati e l'intestazione del suo marchio.",
            "Senza riscrivere niente e senza duplicare il prodotto: se cambi la formula, cambia per tutti i clienti insieme.",
          ]}
          image={IMG.privateLabel}
          alt="Una formula cosmetica Private Label declinata in tre brand diversi"
          imageTestId="landing-private-label-image"
          reverse={false}
          tone="dark"
        />
        <FeatureRow
          sectionTestId="landing-supplier-orders-section"
          title={<>Gli ordini ai fornitori partono da <em className="text-[#8A8579]">qui</em></>}
          paragraphs={[
            "A ogni prodotto associ il fornitore della materia prima. Scegli la quantità e mandi l'ordine dalla piattaforma: niente mail da ritrovare, niente telefonate che non ha scritto nessuno. Resta tutto tracciato, accanto al prodotto.",
          ]}
          image={IMG.supplier}
          alt="Ordine a un fornitore di materie prime gestito in Cosmely, accanto a prodotti skincare"
          reverse={true}
        />
        <WhatChanges />
        <FeatureRow
          id="catalogo-digitale"
          sectionTestId="landing-catalog-section"
          headingTestId="landing-catalog-heading"
          title={<>Il tuo catalogo, <em className="text-[#8A8579]">pronto da condividere.</em></>}
          paragraphs={[
            "Ogni brand ha il suo catalogo digitale: foto e informazioni dei prodotti in una vetrina sempre aggiornata, da aprire sul telefono senza installare nulla.",
            "Condividi il link con clienti e rivenditori, oppure mostra il QR del catalogo. Un gesto, e tutta la tua gamma è a portata di mano.",
          ]}
          image={IMG.catalog}
          alt="Catalogo digitale di cosmetici aperto sul telefono, con icona di condivisione e QR del brand"
          imageTestId="landing-catalog-image"
          reverse={true}
          tone="soft"
          action={{ href: "#demo", label: "Richiedi una demo", testId: "landing-catalog-demo-link" }}
        />
        <ForWho />
        <DemoForm />
      </main>
      <Footer />
    </div>
  );
}
